/**
 * Synthetic environment-layer injection — mirrors the planned
 * `defaultBasemap.ts` pattern for the `"environment"` SceneSlot.
 *
 * Environment layers are camera-relative atmospheric effects (sun, fog,
 * rain, etc.) injected locally via `maybeInjectDefaultEnvironment`.
 * They are never persisted to the gTwin API; the PATCH batcher skips
 * them via `isSyntheticEnvironmentLayer()`.
 *
 * SSOT: `design/10-architecture/information-architect/environment-taxonomy.md`
 *       §2 EnvironmentKind, §3 parameter schemas, §6 default catalog.
 *
 * ---------------------------------------------------------------------------
 * NOTE: `layerMetadata.ts` does not exist in the current codebase (removed
 * in recent refactoring). Once it is (re-)created, register
 * `"environment"` in `SLOT_ORDER` and `SLOT_LABEL` there.
 * ---------------------------------------------------------------------------
 *
 * ---------------------------------------------------------------------------
 * TwinShell integration (for ui-implementation / visualization-engineer):
 *
 * 1. `isLocalOnlyLayer` in TwinShell.tsx MUST also return true for
 *    `isSyntheticEnvironmentLayer(layer)` so the PATCH batcher skips
 *    environment layers — prevents 400 errors to the gTwin API.
 *
 * 2. `maybeInjectDefaultEnvironment` MUST be called alongside
 *    `maybeInjectDefaultBasemap` (once it exists) in the scene-load
 *    path so every gTwin receives the default 12 environment layers
 *    even when the saved scene has no `environment` array.
 *
 * 3. Environment layers MUST participate in `setLayerVisible` and
 *    `setLayerOpacity` the same way basemap layers do. The engine
 *    controller translates opacity into effect-specific mutations
 *    (e.g. sun opacity → DirectionalLight intensity multiplier).
 * ---------------------------------------------------------------------------
 *
 * ---------------------------------------------------------------------------
 * Mount policy (plateauMountPolicy.ts does not exist in current codebase):
 *
 * When `plateauMountPolicy.ts` is (re-)created, apply these rules for
 * environment layers:
 *   - `getPlateauMountTier`: environment layers get tier 2 (between
 *     basemap=0 and roads=10).
 *   - `isPlateauStickyMountLayer`: environment layers ARE sticky
 *     (return true) — never unmount on zoom.
 *   - `isPlateauDeferrableDetailLayer`: environment layers are NEVER
 *     deferrable (return false early) — no tile cache cost.
 *
 * Until that file exists, the helpers below (`getEnvironmentMountTier`,
 * `isEnvironmentStickyMount`, `isEnvironmentDeferrable`) encode the
 * policy for downstream consumers.
 * ---------------------------------------------------------------------------
 */

import type { Layer } from "@geosoft/viewer-sdk";
import type { SceneSlot } from "@/sdk-stub/gtwin-sdk";
import { tokens } from "@/design/tokens";

// ---------------------------------------------------------------------------
// §2 — EnvironmentKind enum (taxonomy SSOT)
// ---------------------------------------------------------------------------

export type EnvironmentKind =
  | "sky"
  | "sun"
  | "moon"
  | "stars"
  | "fog"
  | "clouds"
  | "wind"
  | "rain"
  | "snow"
  | "lightning"
  | "darkmode"
  | "time-of-day";

// ---------------------------------------------------------------------------
// §3 — Per-kind parameter interfaces (taxonomy SSOT)
// ---------------------------------------------------------------------------

export interface SkyParameters {
  mode: "clear" | "overcast" | "dusk" | "night" | "custom";
  intensity: number;
  fogDensity: number;
  fogTint: string;
}

export interface SunParameters {
  hour: number;
  decoupled: boolean;
  intensity: number;
  color: "warm" | "neutral" | "cool";
  castShadows: boolean;
  shadowMapSize: 512 | 1024 | 2048 | 4096;
}

export interface MoonParameters {
  intensity: number;
  phase: number;
  color: "coldBlue" | "neutral";
}

export interface StarsParameters {
  density: "low" | "medium" | "high";
  magnitudeLimit: number;
  twinkle: boolean;
  autoSuppress: boolean;
}

export interface FogParameters {
  density: number;
  colour: string;
  near: number;
  far: number;
  mode: "exp" | "exp2" | "linear";
}

export interface CloudsParameters {
  coverage: number;
  altitude: number;
  speed: number;
  direction: number;
  opacity: number;
}

export interface WindParameters {
  speed: number;
  direction: number;
  gust: boolean;
  particleDensity: "low" | "medium" | "high";
  /** Streak length multiplier (visual). */
  streakLength: number;
  /** Layer opacity 0..1. */
  opacity: number;
}

export interface RainParameters {
  intensity: number;
  windCoupled: boolean;
  puddles: boolean;
  /** Drop/streak size multiplier (visual). */
  dropSize: number;
  /** Fall-speed multiplier. */
  fallSpeed: number;
  /** Layer opacity 0..1. */
  opacity: number;
}

export interface SnowParameters {
  intensity: number;
  windCoupled: boolean;
  accumulation: number;
  /** Flake size multiplier (visual). */
  flakeSize: number;
  /** Fall-speed multiplier. */
  fallSpeed: number;
  /** Horizontal drift/sway multiplier. */
  sway: number;
  /** Flake shape. */
  shape: "dot" | "hex" | "square";
  /** Layer opacity 0..1. */
  opacity: number;
}

export interface LightningParameters {
  frequency: number;
  intensity: number;
  coupledWithRain: boolean;
}

export interface DarkmodeParameters {
  enabled: boolean;
  intensity: number;
}

export interface TimeOfDayParameters {
  mode: "manual" | "project-clock" | "system-clock";
  playbackSpeed: number;
  loop: boolean;
  currentHour: number;
}

export type EnvironmentParameters =
  | SkyParameters
  | SunParameters
  | MoonParameters
  | StarsParameters
  | FogParameters
  | CloudsParameters
  | WindParameters
  | RainParameters
  | SnowParameters
  | LightningParameters
  | DarkmodeParameters
  | TimeOfDayParameters;

// ---------------------------------------------------------------------------
// §7 — Preset definitions (taxonomy SSOT)
// ---------------------------------------------------------------------------

export type EnvironmentPresetId =
  | "clear"
  | "overcast"
  | "dusk"
  | "night"
  | "storm"
  | "custom";

// ---------------------------------------------------------------------------
// §6 — Default layer catalog
// ---------------------------------------------------------------------------

const ENV_ID_PREFIX = "layer:environment:";

export const DEFAULT_ENVIRONMENT_LAYER_IDS: Record<EnvironmentKind, string> = {
  sky:          `${ENV_ID_PREFIX}sky`,
  sun:          `${ENV_ID_PREFIX}sun`,
  moon:         `${ENV_ID_PREFIX}moon`,
  stars:        `${ENV_ID_PREFIX}stars`,
  fog:          `${ENV_ID_PREFIX}fog`,
  clouds:       `${ENV_ID_PREFIX}clouds`,
  wind:         `${ENV_ID_PREFIX}wind`,
  rain:         `${ENV_ID_PREFIX}rain`,
  snow:         `${ENV_ID_PREFIX}snow`,
  lightning:    `${ENV_ID_PREFIX}lightning`,
  darkmode:     `${ENV_ID_PREFIX}darkmode`,
  "time-of-day": `${ENV_ID_PREFIX}time-of-day`,
};

interface EnvironmentLayerSpec {
  kind: EnvironmentKind;
  label: string;
  defaultVisible: boolean;
  defaultParameters: EnvironmentParameters;
}

const DEFAULT_CATALOG: EnvironmentLayerSpec[] = [
  {
    kind: "sky",
    label: "Sky & Atmosphere",
    defaultVisible: true,
    defaultParameters: {
      mode: "clear",
      intensity: 1.0,
      fogDensity: 0.0002,
      fogTint: tokens.color.environment.fog.neutral,
    } satisfies SkyParameters,
  },
  {
    kind: "sun",
    label: "Sun & Daylight",
    defaultVisible: true,
    defaultParameters: {
      hour: 13.0,
      decoupled: false,
      intensity: 2.0,
      color: "neutral",
      castShadows: true,
      shadowMapSize: 2048,
    } satisfies SunParameters,
  },
  {
    kind: "moon",
    label: "Moon & Night Light",
    defaultVisible: false,
    defaultParameters: {
      intensity: 0.5,
      phase: 0.5,
      color: "coldBlue",
    } satisfies MoonParameters,
  },
  {
    kind: "stars",
    label: "Stars / Skybox",
    defaultVisible: false,
    defaultParameters: {
      density: "medium",
      magnitudeLimit: 5.0,
      twinkle: false,
      autoSuppress: true,
    } satisfies StarsParameters,
  },
  {
    kind: "fog",
    label: "Distance Fog",
    defaultVisible: true,
    defaultParameters: {
      density: 0.0002,
      colour: tokens.color.environment.fog.neutral,
      near: 100,
      far: 2000,
      mode: "exp2",
    } satisfies FogParameters,
  },
  {
    kind: "clouds",
    label: "Cloud Cover",
    defaultVisible: false,
    defaultParameters: {
      coverage: 0.5,
      altitude: 2000,
      speed: 2.0,
      direction: 270,
      opacity: 0.8,
    } satisfies CloudsParameters,
  },
  {
    kind: "wind",
    label: "Wind",
    defaultVisible: false,
    defaultParameters: {
      speed: 5.0,
      direction: 270,
      gust: false,
      particleDensity: "medium",
      streakLength: 1.0,
      opacity: 0.5,
    } satisfies WindParameters,
  },
  {
    kind: "rain",
    label: "Rain",
    defaultVisible: false,
    defaultParameters: {
      intensity: 0.5,
      windCoupled: true,
      puddles: false,
      dropSize: 1.0,
      fallSpeed: 1.0,
      opacity: 0.5,
    } satisfies RainParameters,
  },
  {
    kind: "snow",
    label: "Snow",
    defaultVisible: false,
    defaultParameters: {
      intensity: 0.3,
      windCoupled: true,
      accumulation: 0.0,
      flakeSize: 1.0,
      fallSpeed: 1.0,
      sway: 1.0,
      shape: "dot",
      opacity: 0.85,
    } satisfies SnowParameters,
  },
  {
    kind: "lightning",
    label: "Lightning",
    defaultVisible: false,
    defaultParameters: {
      frequency: 2.0,
      intensity: 0.8,
      coupledWithRain: true,
    } satisfies LightningParameters,
  },
  {
    kind: "darkmode",
    label: "Dark Mode / Night Scene",
    defaultVisible: false,
    defaultParameters: {
      enabled: false,
      intensity: 0.7,
    } satisfies DarkmodeParameters,
  },
  {
    kind: "time-of-day",
    label: "Time-of-Day Controller",
    defaultVisible: false,
    defaultParameters: {
      mode: "manual",
      playbackSpeed: 1.0,
      loop: false,
      currentHour: 13.0,
    } satisfies TimeOfDayParameters,
  },
];

/**
 * Creates the 12 default environment Layer objects for synthetic injection.
 */
export function createDefaultEnvironmentLayers(): Layer[] {
  return DEFAULT_CATALOG.map((spec): Layer => ({
    id: DEFAULT_ENVIRONMENT_LAYER_IDS[spec.kind],
    label: spec.label,
    kind: "environment",
    tenantId: "local",
    stacItemId: "synthetic",
    crs: "EPSG:4326",
    bbox: [0, 0, 0, 0],
    endpoints: {},
    visibility: {},
    permissions: { roles: [] },
    style: {
      environmentKind: spec.kind,
      isSyntheticEnvironment: true,
      environmentParameters: spec.defaultParameters,
      family: "environment",
    },
  }));
}

// ---------------------------------------------------------------------------
// Synthetic injection
// ---------------------------------------------------------------------------

/**
 * Visibility defaults: sky, sun ON; everything else OFF.
 *
 * Fog is intentionally OFF by default: its pale exp2 haze (#C8D8E8) washes
 * the entire scene from typical viewing heights, masking draped data layers
 * (e.g. flat DEM color drapes) and making them appear invisible. Users can
 * re-enable fog from the Environment tool card when atmospheric depth is
 * desired.
 */
const DEFAULT_VISIBLE_KINDS: ReadonlySet<EnvironmentKind> = new Set([
  "sky",
  "sun",
]);

export interface InjectionResult {
  layers: Layer[];
  slotEntries: Array<{ layerId: string; slot: SceneSlot }>;
  visibilityEntries: Array<{ layerId: string; visible: boolean }>;
  injected: boolean;
}

/**
 * Injects default environment layers if the scene has none.
 *
 * Same contract as the planned `maybeInjectDefaultBasemap`:
 * - If `slotEntries` already contains any `"environment"` slot entry,
 *   returns unchanged.
 * - Otherwise prepends the 12 default layers, slot entries, and
 *   visibility entries.
 */
export function maybeInjectDefaultEnvironment(
  layers: Layer[],
  slotEntries: Array<{ layerId: string; slot: SceneSlot }>,
  visibilityEntries: Array<{ layerId: string; visible: boolean }>,
): InjectionResult {
  const hasEnvironmentSlot = slotEntries.some(
    (entry) => entry.slot === "environment",
  );

  if (hasEnvironmentSlot) {
    return { layers, slotEntries, visibilityEntries, injected: false };
  }

  const envLayers = createDefaultEnvironmentLayers();

  const envSlotEntries = envLayers.map((layer) => ({
    layerId: layer.id,
    slot: "environment" as SceneSlot,
  }));

  const envVisibilityEntries = envLayers.map((layer) => {
    const envKind = (layer.style as Record<string, unknown>)
      .environmentKind as EnvironmentKind;
    return {
      layerId: layer.id,
      visible: DEFAULT_VISIBLE_KINDS.has(envKind),
    };
  });

  return {
    layers: [...envLayers, ...layers],
    slotEntries: [...envSlotEntries, ...slotEntries],
    visibilityEntries: [...envVisibilityEntries, ...visibilityEntries],
    injected: true,
  };
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

/**
 * Returns true for layers injected by `maybeInjectDefaultEnvironment`.
 * Checks the ID prefix OR the `style.isSyntheticEnvironment` marker.
 */
export function isSyntheticEnvironmentLayer(layer: Layer): boolean {
  if (layer.id.startsWith(ENV_ID_PREFIX)) return true;
  return (layer.style as Record<string, unknown> | undefined)
    ?.isSyntheticEnvironment === true;
}

/**
 * Returns true for any environment-family layer (synthetic or future
 * user-authored).
 */
export function isEnvironmentLayer(layer: Layer): boolean {
  if (layer.id.startsWith(ENV_ID_PREFIX)) return true;
  return (layer.style as Record<string, unknown> | undefined)
    ?.family === "environment";
}

// ---------------------------------------------------------------------------
// Mount policy helpers (until plateauMountPolicy.ts is restored)
// ---------------------------------------------------------------------------

/**
 * Environment layers sit at tier 2 — between basemap (0) and roads (10).
 * Per taxonomy §6, individual kinds have finer sub-tiers (1–9) but the
 * coarse tier used by the mount scheduler is 2 for all environment layers.
 */
export function getEnvironmentMountTier(_layer: Layer): number {
  return 2;
}

/** Environment layers are sticky — never unmount on zoom changes. */
export function isEnvironmentStickyMount(_layer: Layer): boolean {
  return true;
}

/** Environment layers are never deferrable — no tile cache cost. */
export function isEnvironmentDeferrable(_layer: Layer): boolean {
  return false;
}
